import React, { ChangeEvent, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useMst } from "../../mobx";
import { CircularProgress, Grid, Pagination, Typography } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { parse, stringify } from "query-string";
import { QuestionQuery } from "../../api/questions/typings";
import { QuestionsRenderFilters } from "./components/patterns/QuestionsRenderFilters";
import { QuestionRender } from "./components/patterns/QuestionRender";
import { PaginationProps } from "@mui/material/Pagination/Pagination";
import { Nullable } from "../../@typings/typings";
import { useQueryParamsEnhanced } from "../../helpers/hooks/useQueryParamsEnhanced";

export interface RenderFiltersFormData {
	numbers: string[];
	topics: {
		id: string;
		title: string;
	}[];
	skills: {
		id: string;
		title: string;
	}[];
	difficulties: { id: string; title: string }[];
}

export default observer(function () {
	const { questionsStore } = useMst();
	const { total } = questionsStore.page;
	const { getQuestions, getQuestionsFullRenderData } = questionsStore;
	const [loading, setLoading] = useState(false);
	const history = useHistory();
	const params = useQueryParamsEnhanced();
	const page = params["page[number]"];
	const size = params["page[size]"];

	useEffect(() => {
		if (!page || !size) {
			history.push({
				search: stringify({
					...params,
					"page[size]": "15",
					"page[number]": "1",
				}),
			});
		} else {
			getQuestionsAsync();
		}

		async function getQuestionsAsync() {
			setLoading(true);
			await getQuestions({
				...params,
				"page[size]": size,
				"page[number]": page,
			});
			setLoading(false);
		}
	}, [page, size]);

	const onSearch = async (data: RenderFiltersFormData) => {
		const filters: QuestionQuery = {};
		const { skills, topics, numbers, difficulties } = data;
		filters["filter[number]"] = numbers?.length ? numbers : undefined;
		filters["filter[difficulty]"] = difficulties?.length ? difficulties.map((difficulty) => difficulty.id) : undefined;
		filters["filter[skills][]"] = skills?.length ? skills.map((skill) => skill.id) : undefined;
		filters["filter[topics][]"] = topics?.length ? topics.map((topic) => topic.id) : undefined;
		setLoading(true);
		await getQuestions({
			...filters,
			"page[size]": "15",
			"page[number]": "1",
		});
		setLoading(false);
		history.push({
			search: stringify({ ...params, ...filters, "page[size]": "15", "page[number]": "1" }),
		});
	};

	return (
		<Grid container sx={() => ({ width: "100%" })}>
			<QuestionsRenderFilters onSearch={onSearch} />
			{loading ? (
				<Grid container item xs={12} sx={(theme) => ({ padding: theme.spacing(8, 0) })} justifyContent={"center"}>
					<CircularProgress size={80} />
				</Grid>
			) : total ? (
				<>
					<RenderPagination
						page={page ? parseFloat(page) : undefined}
						rowsPerPage={size ? parseFloat(size) : undefined}
						count={Math.ceil(total! / parseFloat(size!))}
					/>
					{getQuestionsFullRenderData.map((question) => (
						<QuestionRender key={question.title} question={question} loading={loading} />
					))}
					<RenderPagination
						page={page ? parseFloat(page) : undefined}
						rowsPerPage={size ? parseFloat(size) : undefined}
						count={Math.ceil(total! / parseFloat(size!))}
					/>
				</>
			) : (
				<Grid container justifyContent={"center"} sx={(theme) => ({ margin: theme.spacing(4, 0) })}>
					<Typography variant={"h6"}>Не нашли доступных задач по заданным фильтрам</Typography>
				</Grid>
			)}
		</Grid>
	);
});

interface RenderPaginationProps extends PaginationProps {
	rowsPerPage: Nullable<number>;
}

function RenderPagination({ page, count, rowsPerPage }: RenderPaginationProps) {
	const history = useHistory();
	const location = useLocation();
	const params = parse(location.search) as QuestionQuery;

	const onPageChange = (_: ChangeEvent<unknown>, page: number) => {
		history.push({
			search: stringify({
				...params,
				"page[size]": rowsPerPage,
				"page[number]": page.toString(),
			}),
		});
	};

	return (
		<Pagination sx={(theme) => ({ margin: theme.spacing(2, 0) })} page={page} count={count} onChange={onPageChange} />
	);
}
